<template>
  <div class="d-flex flex-column fill-height">
    <!-- Content -->
    <v-card-text class="d-flex flex-column flex-grow-1 pa-4 ai-ask-content">
      <div v-if="loading" class="text-center">
        <!-- Circular Loader for ask view -->
        <v-progress-circular indeterminate color="primary" size="40" />
      </div>

      <div v-else class="d-flex flex-column fill-height">
        <!-- Question Icon and Title -->
        <div class="text-center mb-8 mt-16">
          <div class="mb-4">
            <v-icon size="80" icon="$helpLarge" />
          </div>
          <h3 class="headline5 mb-4">{{ $t("aiAssistant.ask.heroTitle") }}</h3>
          <a
            href="https://docs.coro.net/"
            target="_blank"
            class="ai-ask-docs-link d-inline-flex align-center px-2 py-1"
          >
            <v-icon size="16" class="mr-1" icon="$link" />
            <span class="body3">{{ $t("aiAssistant.ask.productDocs") }}</span>
          </a>
        </div>

        <!-- Spacer to push suggestions and input to bottom -->
        <div class="flex-grow-1"></div>

        <!-- Suggestions Section -->
        <div class="d-flex flex-column align-end ga-2 mt-3">
          <v-chip
            v-for="(suggestion, index) in suggestions"
            :key="index"
            variant="outlined"
            size="small"
            class="ai-summary-suggestion-chip body3"
            @click="selectSuggestion(suggestion)"
          >
            <span class="body3">{{ suggestion }}</span>
          </v-chip>
        </div>
      </div>
    </v-card-text>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed } from "vue";
import { storeToRefs } from "pinia";
import { useAiAssistantStore } from "@/_store/ai-assistant.module";
import { useI18n } from "vue-i18n";

const AI_ASSISTANT_SUGGESTIONS = [
  "aiAssistant.suggestions.checkBlockedEmail",
  "aiAssistant.suggestions.searchBlockedEmail",
  "aiAssistant.suggestions.whitelistEmail",
  "aiAssistant.suggestions.maxAttachmentSize",
  "aiAssistant.suggestions.secureMessagesDuration",
  "aiAssistant.suggestions.addNewUsers",
  "aiAssistant.suggestions.signIn",
  "aiAssistant.suggestions.inactiveUser",
  "aiAssistant.suggestions.inactiveUserProtected",
  "aiAssistant.suggestions.unprotectedUser",
  "aiAssistant.suggestions.protectedUser",
  "aiAssistant.suggestions.installAgent",
  "aiAssistant.suggestions.downloadAgent",
  "aiAssistant.suggestions.checkAgentInstalled",
  "aiAssistant.suggestions.addDeviceProtection",
  "aiAssistant.suggestions.removeDeviceProtection",
  "aiAssistant.suggestions.rebootShutdownDevice",
  "aiAssistant.suggestions.allowPersonalData",
  "aiAssistant.suggestions.activityLog",
  "aiAssistant.suggestions.workspaceId",
  "aiAssistant.suggestions.onboardChildWorkspace",
  "aiAssistant.suggestions.applyRulesChildWorkspaces",
  "aiAssistant.suggestions.spamEmailsCoro",
  "aiAssistant.suggestions.encryptionKey",
];

export default defineComponent({
  emits: ["close", "back", "showChat", "suggestionSelected"],
  setup(_, { emit }) {
    const { t } = useI18n();
    const aiAssistantStore = useAiAssistantStore();
    const { loading } = storeToRefs(aiAssistantStore);

    const suggestions = computed(() => {
      // Shuffle the array and take the first 3 suggestions
      const shuffled = [...AI_ASSISTANT_SUGGESTIONS].sort(() => Math.random() - 0.5);
      return shuffled.slice(0, 3).map((key) => t(key));
    });

    const selectSuggestion = (suggestion: string) => {
      emit("suggestionSelected", suggestion);
    };

    return {
      loading,
      suggestions,
      selectSuggestion,
    };
  },
});
</script>

<style lang="scss" scoped>
.ai-ask-content {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.ai-ask-docs-link {
  background-color: rgba(var(--v-theme-blue-light), 0.1) !important;
  border-radius: 4px;
}

.ai-summary-suggestion-chip {
  width: fit-content;
  justify-content: flex-start !important;
  border: 1px solid rgb(var(--v-theme-indigo-pale)) !important;
  border-radius: 4px;
}
</style>
